import { callAct, isOk, statusOf } from './apiConfig';

/*
 * reservations — thin adapter over the PHP reservation/availability acts, so the LIVE
 * board and booking flows call real endpoints. Where an act does not exist yet
 * (reservationsList / reservationAssignTable / reservationSetStatus are NEW in
 * docs/BACKEND_ACTS_SPEC.md) the calls FAIL GRACEFULLY and the caller falls back to its
 * mock/local data, so the UI keeps working and flips to real data the moment the act ships.
 *
 * Normalized client reservation shape (what the board consumes):
 *   { id, time:"HH:MM", name, party, phone, status, area, tableId, durationMin }
 */

const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Map a PHP reservation row → client shape (best-effort; tolerant of field names).
function normalize(row) {
    return {
        id: row.id ?? row.ID ?? row.reservationID,
        date: row.date ?? '',
        time: row.time ?? row.startTime ?? '',
        name: row.name ?? [row.privateName, row.lastName].filter(Boolean).join(' ').trim(),
        party: Number(row.party ?? row.dinersNumber ?? 0) || 0,
        phone: row.phone ?? '',
        status: row.status ?? 'confirmed',
        area: row.area ?? row.areaName ?? '',
        tableId: row.tableId ?? row.tableID ?? row.requestedTableID ?? null,
        durationMin: Number(row.durationMin ?? row.durationMinutes ?? 0) || undefined,
        joinGroupId: Number(row.joinGroupId ?? 0) || 0, // >0 = part of a linked big-group booking
    };
}

/*
 * listToday — today's reservations from the backend, or `mockFallback` if the act is
 * missing/empty/errors. Returns { rows, live } — live=true only when real data came back.
 */
export async function listToday(mockFallback = []) {
    try {
        const envelope = await callAct('reservationsList', { startingDate: todayISO() });
        // A successful response is LIVE even when empty (a genuinely quiet day) — do NOT
        // fall back to mock then, or a quiet real day would show fake reservations.
        if (isOk(envelope) && Array.isArray(envelope.data)) {
            return { rows: envelope.data.map(normalize), live: true };
        }
    } catch (e) { /* act not available yet */ }
    return { rows: mockFallback, live: false };
}

/*
 * listRange — reservations across a date range (the calendar / יומן). Same normalized shape
 * as listToday; returns { rows, live } (live=true only when real data came back).
 */
export async function listRange({ startingDate, endingDate }) {
    try {
        const envelope = await callAct('reservationsList', { startingDate, endingDate });
        if (isOk(envelope) && Array.isArray(envelope.data)) {
            return { rows: envelope.data.map(normalize), live: true };
        }
    } catch (e) { /* act not available */ }
    return { rows: [], live: false };
}

// Server availability for a date/area (getAvailableTimes EXISTS). Returns times[] or null.
export async function getAvailableTimes({ startingDate, countryCode = 'IL', areaID = null }) {
    try {
        const data = { startingDate, countryCode };
        if (areaID != null) data.areaID = areaID;
        const envelope = await callAct('getAvailableTimes', data);
        if (isOk(envelope)) return envelope.data;
    } catch (e) { /* ignore */ }
    return null;
}

/*
 * assignTable — seat a reservation onto a table on the server (act reservationAssignTable).
 * The PHP act reads `ID` (the reservation) + `tableID` (the table). Returns { ok, status } —
 * status is the PHP code so the caller can distinguish a real rejection (409 = table already
 * occupied, 316 = not this business's row) from a transport failure (offline).
 */
export async function assignTable({ reservationId, tableId, seat, auto }) {
    try {
        // auto=1 → the SERVER picks a best-fit free table (no tableID needed). Otherwise a manual table.
        const payload = auto ? { ID: reservationId, auto: 1 } : { ID: reservationId, tableID: tableId };
        // seat=0 → assign the table WITHOUT seating (future/list assignment). Omitting `seat`
        // keeps the server default (seat now) so the LiveBoard walk-in flow is unchanged.
        if (seat === 0 || seat === 1) payload.seat = seat;
        const envelope = await callAct('reservationAssignTable', payload);
        // data.tableID = the table the server assigned (for the auto path). 410 = no free table.
        return { ok: isOk(envelope), status: statusOf(envelope), data: envelope?.data };
    } catch (e) {
        return { ok: false, offline: true };
    }
}

// Manually create a reservation (owner walk-in / phone booking) — reservationCreate act.
// tableId is optional (0 = unassigned); when >0 the server assigns that table on creation.
export async function create({ date, startTime, numberOfGuests, guestName, guestPhone = '', area = 0, guestNotes = '', tableId = 0, waiting = 0, autoTable = 0 }) {
    try {
        const payload = { date, startTime, numberOfGuests, guestName, guestPhone, area, guestNotes };
        if (tableId && Number(tableId) > 0) payload.tableID = Number(tableId);
        else if (autoTable) payload.autoTable = 1; // server picks a best-fit free table
        if (waiting) payload.waiting = 1; // a רשימת המתנה entry (no table, liveStatus 'waiting')
        const envelope = await callAct('reservationCreate', payload);
        // data.tableID = the assigned table (0 if none), data.autoAssigned = server auto-picked it.
        return { ok: isOk(envelope), status: statusOf(envelope), data: envelope?.data };
    } catch (e) {
        return { ok: false, offline: true };
    }
}

// The waiting list (רשימת המתנה) — today's walk-ins waiting for a table. Returns [] on error.
export async function waitingList() {
    try {
        const envelope = await callAct('waitingList', {});
        if (isOk(envelope) && Array.isArray(envelope.data)) return envelope.data.map(normalize);
    } catch (e) { /* ignore */ }
    return [];
}

// Same as waitingList() but SIGNALS load failures: { ok, rows }. A screen with a real error
// state (the waitlist screen) uses this so a backend outage shows an error, not a false "empty".
export async function waitingListResult() {
    try {
        const envelope = await callAct('waitingList', {});
        if (isOk(envelope) && Array.isArray(envelope.data)) return { ok: true, rows: envelope.data.map(normalize) };
    } catch (e) { /* ignore */ }
    return { ok: false, rows: [] };
}

// Remove a waiting entry (soft-delete server-side — leaves both lists, no cancel notification).
export async function waitingRemove(reservationId) {
    try {
        const envelope = await callAct('waitingRemove', { ID: reservationId });
        return { ok: isOk(envelope) };
    } catch (e) { return { ok: false }; }
}

// One table's reservation history (the board's per-table history panel). Returns [] on error.
export async function tableHistory(tableId) {
    try {
        const envelope = await callAct('tableHistory', { tableID: tableId });
        if (isOk(envelope) && Array.isArray(envelope.data)) return envelope.data;
    } catch (e) { /* ignore */ }
    return [];
}

// Edit a reservation's GUEST DETAILS (the unified Reservation Drawer, HOP-031). Sends ONLY the
// fields provided so the server touches nothing else (name/phone/party/notes/area). Table, status,
// date & time are edited via their own acts. Returns { ok, status, data } (data = fresh brief).
export async function update({ reservationId, guestName, guestPhone, numberOfGuests, guestNotes, area }) {
    try {
        const payload = { ID: reservationId };
        if (guestName !== undefined) payload.guestName = guestName;
        if (guestPhone !== undefined) payload.guestPhone = guestPhone;
        if (numberOfGuests !== undefined) payload.numberOfGuests = Number(numberOfGuests);
        if (guestNotes !== undefined) payload.guestNotes = guestNotes;
        if (area !== undefined) payload.area = Number(area);
        const envelope = await callAct('reservationUpdate', payload);
        return { ok: isOk(envelope), status: statusOf(envelope), data: envelope?.data };
    } catch (e) {
        return { ok: false, offline: true };
    }
}

// Set a reservation's live status (arrived / seated / no-show / cancelled).
// PHP whitelists inputs: the reservation id goes as `ID`, the status as `liveStatus`.
export async function setStatus({ reservationId, status }) {
    try {
        const envelope = await callAct('reservationSetStatus', { ID: reservationId, liveStatus: status });
        return { ok: isOk(envelope) };
    } catch (e) {
        return { ok: false, offline: true };
    }
}

// ── TEMPORARY HOLD (שמירה זמנית) — lock a table while someone is booking it. ──
// Place a hold on a table. Returns { ok, status, data:{ID, token, expiresInMinutes} }. 409=taken, 418=full.
export async function hold({ tableId, date, startTime, numberOfGuests }) {
    try {
        const envelope = await callAct('reservationHold', { tableID: tableId, date, startTime, numberOfGuests });
        return { ok: isOk(envelope), status: statusOf(envelope), data: envelope?.data };
    } catch (e) { return { ok: false }; }
}
// Confirm a hold → a real booking. Returns { ok, status }. 409=taken meanwhile, 410=expired, 311=wrong token.
export async function holdConfirm({ holdId, token, guestName, guestPhone = '', numberOfGuests }) {
    try {
        const envelope = await callAct('reservationHoldConfirm', { ID: holdId, token, guestName, guestPhone, numberOfGuests });
        return { ok: isOk(envelope), status: statusOf(envelope), data: envelope?.data };
    } catch (e) { return { ok: false }; }
}
// Release a hold (the holder gives up the table). Best-effort/idempotent.
export async function holdRelease(holdId, token) {
    try {
        const envelope = await callAct('reservationHoldRelease', { ID: holdId, token });
        return { ok: isOk(envelope) };
    } catch (e) { return { ok: false }; }
}

// Link several reservations into ONE big-group booking (multi-table join). ids = reservation IDs.
export async function linkGroup(ids) {
    try {
        const envelope = await callAct('reservationLinkGroup', { joinTables: ids });
        return { ok: isOk(envelope), data: envelope?.data };
    } catch (e) { return { ok: false }; }
}
// Set liveStatus for a whole linked group at once (e.g. cancel the group).
export async function groupSetStatus(joinGroupId, status) {
    try {
        const envelope = await callAct('reservationGroupSetStatus', { joinGroupId, liveStatus: status });
        return { ok: isOk(envelope) };
    } catch (e) { return { ok: false }; }
}

export default { listToday, listRange, getAvailableTimes, assignTable, setStatus, update, create, tableHistory, waitingList, waitingListResult, waitingRemove, linkGroup, groupSetStatus, hold, holdConfirm, holdRelease };
